import React, { useId } from 'react'
import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

/**
 * ## SchedulesCenterSelect component
 * Select component for choosing the Scheduling Center (Centro de Escalas).
 *
 * @param {string} value - Current selected center value
 * @param {string[]} options - Array of center options (value-label pairs or just strings)
 * @param {(event: SelectChangeEvent) => void} onChange - Handler for value changes
 * @param {boolean} [disabled=false] - Whether the select is disabled
 * @returns {JSX.Element} The rendered SchedulesCenterSelect component
 */
interface SchedulesCenterSelectProps {
	value: string
	options: Array<{ value: string; label: string }> | string[]
	onChange: (event: SelectChangeEvent) => void
	disabled?: boolean
	label?: string
	width?: string
}

const SchedulesCenterSelect = React.memo(function SchedulesCenterSelect({
	value,
	options,
	onChange,
	disabled = false,
	label = 'Centro de Escalas',
	width = '240px',
}: SchedulesCenterSelectProps) {
	const selectId = useId()
	const labelId = `${selectId}-label`

	// Normalize options to always have value and label
	const normalizedOptions = React.useMemo(() => {
		return options.map(option => {
			if (typeof option === 'string') {
				return { value: option, label: option }
			}
			return option
		})
	}, [options])

	return (
		<FormControl
			size='small'
			variant='outlined'
			disabled={disabled}
			sx={{
				width,
				'& .MuiOutlinedInput-root': {
					height: '40px',
					fontFamily: 'Poppins',
					backgroundColor: '#FFFFFF',
					'& .MuiSelect-select': {
						fontFamily: 'Poppins',
						fontWeight: 500,
						fontSize: '13px',
						color: '#092056',
						padding: '12px 32px 8px 12px',
						lineHeight: '20px',
						backgroundColor: '#FFFFFF',
					},
					'& fieldset': {
						borderColor: 'rgba(134, 149, 175, 0.3)',
					},
					'&:hover': {
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: 'rgba(134, 149, 175, 0.3)',
						},
					},
					'&.Mui-focused': {
						backgroundColor: '#FFFFFF',
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: 'rgba(134, 149, 175, 0.3)',
						},
					},
				},
				'& .MuiInputLabel-root': {
					fontFamily: 'Poppins',
					fontWeight: 400,
					fontSize: '11px',
					lineHeight: '12px',
					color: '#092056',
					backgroundColor: '#FFFFFF',
					padding: '0 4px',
					transform: 'translate(12px, -6px) scale(1)',
					transformOrigin: 'top left',
				},
			}}
		>
			<InputLabel id={labelId}>{label}</InputLabel>
			<Select
				labelId={labelId}
				id={selectId}
				value={value}
				label={label}
				onChange={onChange}
				IconComponent={() => (
					<KeyboardArrowDownIcon
						sx={{
							color: '#00A5FF',
							marginRight: '8px',
						}}
					/>
				)}
			>
				{normalizedOptions.map(option => (
					<MenuItem
						key={option.value}
						value={option.value}
						sx={{
							fontFamily: 'Poppins',
							fontSize: '13px',
							fontWeight: 500,
							color: '#092056',
						}}
					>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)
})

export default SchedulesCenterSelect
